<template>
  <div class="subpage-card">
    <div class="wizard wizard--page">
      <StepperRail
        :steps="steps"
        :current="stepIndex"
        :completed="completedSteps"
        @jump="jump"
      />
      <section class="wizard__panel">
        <div class="wizard__head">
          <div>
            <div class="progress">שלב {{ stepIndex + 1 }} מתוך {{ steps.length }}</div>
            <h2>{{ steps[stepIndex].label }}</h2>
            <p v-if="stepIndex === 0">בואו נתחיל מהפרטים הבסיסיים — אפשר תמיד לערוך אחר כך.</p>
            <p v-else-if="stepIndex === 1">העלו תמונות איכותיות — הן יופיעו בעמוד הלינקטון.</p>
            <p v-else-if="stepIndex === 2">קליפ ב-YouTube מגדיל את הסיכוי שהשיר ייבחר לפלייליסטים.</p>
            <p v-else-if="stepIndex === 3">הקומוניקט הוא המסמך שאנשי המדיה מקבלים. מספיק PDF או Word — אחד מהם.</p>
            <p v-else-if="stepIndex === 4">הוסיפו את הרצועות עם קובץ המקור. שאר הפורמטים יופקו אוטומטית.</p>
            <p v-else-if="stepIndex === 5">סקירה אחרונה לפני שליחה. אם משהו חסר — הכרטיס בראש העמוד יציין מה.</p>
            <p v-else-if="stepIndex === 6">צעד אחרון — לאחר השליחה אפשר עדיין להמשיך לערוך.</p>
          </div>
        </div>

        <div class="wizard__body">
          <StepDetails
            v-if="stepIndex === 0"
            :type-meta="typeMeta"
            :artist-name="artistName"
          />
          <StepImages v-else-if="stepIndex === 1" />
          <StepVideo v-else-if="stepIndex === 2" />
          <StepComms v-else-if="stepIndex === 3" />
          <StepTracks v-else-if="stepIndex === 4" :type-meta="typeMeta" />
          <StepReview
            v-else-if="stepIndex === 5"
            :type-meta="typeMeta"
            :artist-name="artistName"
            :balance="currentBalance"
            :missing="missing"
            @jump="jump"
          />
          <StepTerms
            v-else-if="stepIndex === 6"
            :type-meta="typeMeta"
            :balance="currentBalance"
            @update:agreed="setAgreed"
            @go-buy="$emit('go-buy')"
          />
        </div>

        <footer class="wizard__foot">
          <div class="wizard__foot-left">
            <button v-if="stepIndex > 0" type="button" class="btn btn--ghost" @click="prev">
              <LtIcon name="arrow-right" :size="16" /> הקודם
            </button>
          </div>
          <div class="wizard__foot-right">
            <span class="autosave-status" :class="'autosave-status--' + autoSaveStatus">
              <span v-if="autoSaveStatus === 'saving'" class="autosave-status__spinner"></span>
              <LtIcon v-else name="check" :size="13" />
              <span>{{ autoSaveStatus === 'saving' ? 'מעדכן שינויים...' : 'הכל מעודכן' }}</span>
            </span>
            <button v-if="!isLastStep" type="button" class="btn btn--primary" @click="next">
              הבא <LtIcon name="arrow-left" :size="16" />
            </button>
            <button v-else type="button" class="btn btn--primary" :disabled="!canSubmit" @click="submit">
              <LtIcon :name="currentBalance > 0 ? 'wallet' : 'send'" :size="16" />
              {{ submitLabel }}
            </button>
          </div>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import LtIcon from './LtIcon.vue';
import StepperRail from './StepperRail.vue';
import StepDetails from './wizard/StepDetails.vue';
import StepImages from './wizard/StepImages.vue';
import StepVideo from './wizard/StepVideo.vue';
import StepComms from './wizard/StepComms.vue';
import StepTracks from './wizard/StepTracks.vue';
import StepReview from './wizard/StepReview.vue';
import StepTerms from './wizard/StepTerms.vue';
import { LINKTONE_TYPES, WIZARD_STEPS } from './data.js';
import { wizardFormInject } from './wizardInject.js';

export default {
  name: 'WizardPage',
  mixins: [wizardFormInject],
  components: {
    LtIcon,
    StepperRail,
    StepDetails,
    StepImages,
    StepVideo,
    StepComms,
    StepTracks,
    StepReview,
    StepTerms,
  },
  props: {
    balance: { type: Object, required: true },
    artistName: { type: String, default: '' },
    initialStep: { type: Number, default: 0 },
    autoSaveStatus: { type: String, default: 'idle' },
  },
  emits: ['save-draft', 'submit-balance', 'go-buy'],
  data() {
    return {
      stepIndex: this.initialStep,
      visited: [this.initialStep],
      steps: WIZARD_STEPS,
    };
  },
  computed: {
    typeMeta() {
      return LINKTONE_TYPES[this.form.type];
    },
    currentBalance() {
      return this.balance[this.form.type] || 0;
    },
    stepsFilled() {
      const filled = new Set();
      const f = this.form;

      // Step 0 - Details: name + label + releaseDate
      if (f.name) filled.add(0);

      // Step 1 - Images: main + catalog
      if (f.images.main && f.images.catalog) filled.add(1);

      // Step 2 - Video: youtube
      if (f.youtube) filled.add(2);

      // Step 3 - Comms
      if (f.comms.pressReleasePdf && f.comms.pressReleaseDoc && f.comms.lyrics && f.comms.credits && f.comms.creditsText) filled.add(3);

      // Step 4 - Tracks
      if (
        f.tracks.length > 0 &&
        f.tracks.every((t) => t.name && t.audio.mp3_320 && t.audio.mp3_128 && t.audio.mp2 && t.lyrics && t.writer && t.composer)
      ) filled.add(4);

      // Step 5 - Review: filled only if all previous steps are done
      if ([0, 1, 2, 3, 4].every((i) => filled.has(i))) filled.add(5);

      // Step 6 - Terms: agreed
      if (f.agreed) filled.add(6);

      return filled;
    },
    completedSteps() {
      const result = [];
      for (let i = 0; i < this.steps.length; i++) {
        if (i !== this.stepIndex && this.stepsFilled.has(i)) result.push(i);
      }
      return result;
    },
    missing() {
      const m = [];
      if (!this.form.name) m.push({ key: 'name', label: 'שם הלינקטון', step: 0 });
      if (!this.form.images.main) m.push({ key: 'main', label: 'תמונה ראשית', step: 1 });
      if (!this.form.youtube) m.push({ key: 'youtube', label: 'קישור YouTube', step: 2 });
      if (!this.form.comms.pressReleasePdf) m.push({ key: 'pressPdf', label: 'קומוניקט PDF', step: 3 });
      if (!this.form.comms.pressReleaseDoc) m.push({ key: 'pressDoc', label: 'קומוניקט Word', step: 3 });
      if (!this.form.comms.lyrics) m.push({ key: 'lyrics', label: 'מילות שירים', step: 3 });
      if (!this.form.comms.credits) m.push({ key: 'credits', label: 'קרדיטים — קובץ', step: 3 });
      if (!this.form.comms.creditsText) m.push({ key: 'creditsText', label: 'קרדיטים — טקסט', step: 3 });
      if (!this.form.tracks.length) {
        m.push({ key: 'tracks', label: 'לפחות רצועה אחת', step: 4 });
      } else {
        if (this.form.tracks.some((t) => !t.name)) {
          m.push({ key: 'trackName', label: 'שמות לכל הרצועות', step: 4 });
        }
        if (this.form.tracks.some((t) => !t.audio.mp3_320 || !t.audio.mp3_128 || !t.audio.mp2)) {
          m.push({ key: 'audio', label: 'כל פורמטי האודיו (MP3 320, MP3 128, MP2) בכל רצועה', step: 4 });
        }
        if (this.form.tracks.some((t) => !t.lyrics)) {
          m.push({ key: 'trackLyrics', label: 'מילות שיר לכל הרצועות', step: 4 });
        }
        if (this.form.tracks.some((t) => !t.writer)) {
          m.push({ key: 'trackWriter', label: 'שם כותב המילים לכל הרצועות', step: 4 });
        }
        if (this.form.tracks.some((t) => !t.composer)) {
          m.push({ key: 'trackComposer', label: 'שם המלחין לכל הרצועות', step: 4 });
        }
      }
      return m;
    },
    isLastStep() {
      return this.stepIndex === this.steps.length - 1;
    },
    canSubmit() {
      return this.missing.length === 0 && this.form.agreed && this.currentBalance > 0;
    },
    submitLabel() {
      return 'השתמש ביתרה ושלח לאישור';
    },
  },
  methods: {
    setAgreed(value) {
      if (this.form) this.form.agreed = value;
    },
    jump(i) {
      this.stepIndex = i;
      if (!this.visited.includes(i)) this.visited.push(i);
    },
    next() {
      if (this.stepIndex < this.steps.length - 1) this.jump(this.stepIndex + 1);
    },
    prev() {
      if (this.stepIndex > 0) this.jump(this.stepIndex - 1);
    },
    submit() {
      if (!this.canSubmit) return;
      this.$emit('submit-balance');
    },
  },
};
</script>
